import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_IMG =
  "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const headLines = ["Securing Tech.", "Protecting Data.", "Powering Circular."];

  return (
    <section ref={ref} className="relative min-h-screen bg-background pt-28 md:pt-32 overflow-hidden grain">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-2 w-2 bg-primary animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Global ITAD & Data Destruction · HQ Dubai, UAE
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display font-black uppercase tracking-tighter text-foreground text-[11vw] leading-[0.9] md:text-7xl lg:text-[6.2rem] lg:leading-[0.88]">
              {headLines.map((l, i) => (
                <span key={i} className="line-mask">
                  <motion.span
                    className="block"
                    variants={line}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {i === 1 ? <span className="text-primary">{l}</span> : l}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="lg:col-span-4 lg:pb-4"
          >
            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-sm">
              A global provider of IT Asset Disposition, secure data destruction, data center
              decommissioning and technology lifecycle management — delivered across 70+ countries
              under a single point of contact.
            </p>
            <a
              href="#quote"
              data-testid="hero-cta-btn"
              className="group inline-flex items-center gap-2 mt-6 bg-foreground text-background px-6 py-4 font-mono text-xs uppercase tracking-wider hover:bg-primary transition-colors"
            >
              Request Free Assessment
              <ArrowDownRight size={16} className="group-hover:rotate-[-45deg] transition-transform" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-foreground/15"
        >
          {[
            ["70+", "Countries Served"],
            ["1", "Single Point of Contact"],
            ["100%", "Data Destruction Rate"],
            ["5", "Global Offices"],
          ].map(([n, l]) => (
            <div key={l} className="py-6 md:py-8 md:px-6 border-b md:border-b-0 md:border-r border-foreground/15 first:md:pl-0">
              <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">{n}</div>
              <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Floating parallax product frame */}
      <motion.div
        style={{ y }}
        className="pointer-events-none absolute right-0 top-24 hidden xl:block w-[26vw] h-[60vh] overflow-hidden border border-foreground/10"
      >
        <motion.img
          style={{ scale }}
          src={HERO_IMG}
          alt="Data center server racks"
          className="w-full h-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
        <div className="absolute bottom-0 left-0 bg-foreground text-background px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
          Datacenter / Rack-01
        </div>
      </motion.div>
    </section>
  );
}
